import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, ExternalLink, X } from "lucide-react";

/**
 * A reusable PDF preview modal. Renders the PDF via <iframe> so admins can
 * inspect the karne / bildirim before sharing on WhatsApp or downloading.
 *
 * Props:
 *   open (bool), onClose (fn), title (string), url (full absolute PDF URL),
 *   downloadName (optional string — used to hint browser filename).
 */
export default function PdfPreviewModal({ open, onClose, title, url, downloadName }) {
  const download = () => {
    // Force download by creating an anchor with download attribute.
    const a = document.createElement("a");
    a.href = url;
    if (downloadName) a.download = downloadName;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl w-[92vw] h-[90vh] p-0 gap-0 flex flex-col" data-testid="pdf-preview-modal">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-heading font-semibold text-slate-900 text-base">
            {title || "PDF Önizleme"}
          </DialogTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} data-testid="pdf-open-new-tab">
              <ExternalLink className="w-4 h-4 mr-2" /> Yeni sekmede aç
            </Button>
            <Button size="sm" onClick={download} className="bg-[#1E3A8A] hover:bg-[#1e40af]" data-testid="pdf-download-btn">
              <FileDown className="w-4 h-4 mr-2" /> İndir
            </Button>
            <button
              onClick={onClose}
              className="ml-2 w-8 h-8 flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
              aria-label="Kapat"
              data-testid="pdf-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {url ? (
            <iframe
              src={url}
              title={title || "PDF"}
              className="w-full h-full border-0"
              data-testid="pdf-iframe"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm">PDF yükleniyor…</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
